import { useEffect } from 'react';
import { useMatch } from '@tanstack/react-location';
import { DataSourcesQueries } from '@frontend/api-data-sources';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { Trans, useTranslation } from '@frontend/i18n';
import { useSettingsNavigate } from '@frontend/settings-routing';
import { SettingsPage, SettingsPageLayout } from '@frontend/settings-ui';
import { TextLink } from '@frontend/design-system';
import { useSchedulingLocationInfo } from '../../hooks/use-scheduling-location-info';
import { ServiceTypesSettingsTrackingIds } from '../../trackingIds/service-types-settings-tracking';
import { MultiLocationServiceTypes } from './MultiLocationServiceTypes';
import { NewServiceTypeButton } from './NewServiceTypeButton';
import { ServiceTypeV3Page } from './ServiceTypesV3Page';

export const ServiceTypeV3Settings = () => {
  const { t } = useTranslation('schedule-settings');
  const { navigate } = useSettingsNavigate();
  const { params } = useMatch();

  const { getScopeName, selectedLocationIds } = useSchedulingLocationInfo();
  const { isIntegratedOffice, isLoading: isLoadingIntegrations } = DataSourcesQueries.useGetAccessibleIntegrations();
  const locationId = (selectedLocationIds.length === 1 ? selectedLocationIds[0] : params?.locationId) ?? '';

  const handleNavigation = () => {
    if (params?.locationId) {
      navigate({ to: `/schedule/availability/:locationId`, params: { locationId: params.locationId } });
    } else {
      navigate({ to: '/schedule/availability' });
    }
  };

  // Track Pendo event on page view
  useEffect(() => {
    if (!isLoadingIntegrations && locationId) {
      SchedulerV3.trackPendoEvent(ServiceTypesSettingsTrackingIds.events.servicesPageViewed, {
        isIntegrated: isIntegratedOffice,
        locationId,
      });
    }
  }, [isIntegratedOffice, isLoadingIntegrations, locationId]);

  return (
    <SettingsPageLayout>
      <SettingsPage
        settingsLocationConfig={{
          chipLabel: getScopeName(locationId),
          isSingleLocation: !!locationId,
        }}
        title={t('Services')}
        subtitle={
          <Trans t={t}>
            Define what services you offer. Visit {''}
            <TextLink onClick={handleNavigation}>Availability Rules</TextLink>
            {''} to control when, where and how they’re available.
          </Trans>
        }
        action={<NewServiceTypeButton locationId={locationId} />}
        maxWidth='100%'
      >
        {locationId ? (
          <ServiceTypeV3Page locationId={locationId} isIntegratedOffice={isIntegratedOffice} />
        ) : (
          <MultiLocationServiceTypes shouldRenderV3Settings />
        )}
      </SettingsPage>
    </SettingsPageLayout>
  );
};
